import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AiOutlineArrowLeft, AiOutlineExclamationCircle } from 'react-icons/ai';
import apiClient from '../utils/apiClient';
import './SubscriptionStatus.css';
import {
   getSubscriptionStatus, startFreeTrial, subscribeUser
} from "../utils/APIRoutes";

const SubscriptionPage = () => {
  const { userId } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const showErrorToast = (message) => {
    setToastMessage({
      message,
      type: 'error'
    });
    setShowToast(true);
  };

  // SECURITY: swapped `axios` for `apiClient` in all three calls below.
  // getSubscriptionStatus/startFreeTrial/subscribeUser are gated
  // server-side by requireSelfOrAdmin(userId) — the caller must be the
  // same session as the `userId` route/body value (or an admin). A
  // plain axios call carries no session cookie, so these would 401
  // even for the legitimate owner of this subscription. Route names
  // and behavior otherwise unchanged.
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const { data } = await apiClient.get(`${getSubscriptionStatus}/${userId}`);
        setSubscription(data.subscription);
        console.log(data);
        setLoading(false);
      } catch (err) {
        const errorMessage = 'Failed to load subscription status';
        setError(errorMessage);
        showErrorToast(errorMessage);
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, [userId]);

  const startTrial = async () => {
    try {
      const { data } = await apiClient.post(startFreeTrial, { userId });
      setSubscription(data.subscription);
    } catch (err) {
      const errorMessage = 'Failed to start trial or free trial already used. Start monthly or annual plan.';
      setError(errorMessage);
      showErrorToast(errorMessage);
    }
  };

  const subscribe = async () => {
    try {
      const { data } = await apiClient.post(subscribeUser, {
        userId,
        subscriptionType: selectedPlan
      });
      setSubscription(data.subscription);
    } catch (err) {
      const errorMessage = 'Failed to process subscription. Please try again.';
      setError(errorMessage);
      showErrorToast(errorMessage);
    }
  };

  if (loading) return (
    <div style={{display:'flex', justifyContent:'space-between'}}>
      Loading subscription status...
    </div>
  );

  return (
    <div className="subscription-container">
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(220, 38, 38, 0.95)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'fadeIn 0.3s ease-in-out',
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          <AiOutlineExclamationCircle size={24} color="#fef2f2" />
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              Error!
            </div>
            <div style={{ fontSize: '14px' }}>
              {toastMessage?.message}
            </div>
          </div>
        </div>
      )}

      <Link to={'/'}>
        <AiOutlineArrowLeft style={{ 
          position: 'absolute', 
          top: 8, 
          left: 8, 
          fontSize: 24, 
          cursor: 'pointer' 
        }} />
      </Link>

      <h2>Your Subscription Status</h2>
      
      {subscription?.active ? (
        <div className="subscription-active">
          <p>
            <strong>Plan:</strong> {subscription.type} <br />
            <strong>Status:</strong> Active <br />
            <strong>Expires:</strong> {new Date(subscription.endDate).toDateString()}
          </p>
        </div>
      ) : subscription?.type === 'trial' ? (
        <div className="subscription-expired">
          <p>Your free trial has ended. Subscribe to continue posting products.</p>
          <div className="subscription-options">
            <div className="plan-option">
              <input 
                type="radio" 
                id="monthly" 
                name="plan" 
                value="monthly" 
                checked={selectedPlan === 'monthly'}
                onChange={() => setSelectedPlan('monthly')}
              />
              <label htmlFor="monthly">
                <strong>Monthly:</strong> $3/month
              </label>
            </div>
            <div className="plan-option" hidden>
              <input 
                type="radio" 
                id="annual" 
                name="plan" 
                value="annual" 
                checked={selectedPlan === 'annual'}
                onChange={() => setSelectedPlan('annual')}
              />
              <label htmlFor="annual">
                <strong>Annual:</strong> $34/year (save $2)
              </label>
            </div>
            <button className="subscribe-btn" onClick={subscribe}>
              Subscribe Now
            </button>
          </div>
        </div>
      ) : !subscription?.active && !loading ? (
        <div className="no-subscription">
          <p>Start with a 1-month free trial to post products!</p>
          <button className="trial-btn" onClick={startTrial}>
            Start Free Trial
          </button>
          <div className="subscription-options" style={{ marginTop: '2rem' }}>
            <h3>Or choose a paid plan:</h3>
            <div className="plan-option">
              <input 
                type="radio" 
                id="monthly" 
                name="plan" 
                value="monthly" 
                checked={selectedPlan === 'monthly'}
                onChange={() => setSelectedPlan('monthly')}
              />
              <label htmlFor="monthly">
                <strong>Monthly:</strong> $3/month
              </label>
            </div>
            <div className="plan-option" style={{ display: 'none' }}>
              <input 
                type="radio" 
                id="annual" 
                name="plan" 
                value="annual" 
                checked={selectedPlan === 'annual'}
                onChange={() => setSelectedPlan('annual')}
              />
              <label htmlFor="annual">
                <strong>Annual:</strong> $34/year (save $2)
              </label>
            </div>
            <button className="subscribe-btn" onClick={subscribe}>
              <strike>Subscribe Now</strike>
            </button>
            <button className="subscribe-btn" onClick={subscribe}>
              Activate Your Free Offer
            </button>
          </div>
        </div>
      ) : null}
      
      {/* Original error display (kept as fallback) */}
      {error && !showToast && (
        <div className="error">
          <p>{error}</p>
        </div>
      )}
      
      <Link to="/" className="back-link">Back to Home</Link>
    </div>
  );
};

export default SubscriptionPage;
